import axios from "axios";
import { configCurrentSite } from "../utils/config";
import { devLog } from "../utils/devLog";

// Create axios instance without baseURL - will be set dynamically per request
const api = axios.create({
  timeout: 30000, // 30 second timeout
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const axiosConfig = config;

  // Dynamically determine baseURL based on current execution context
  const isServer = typeof window === 'undefined';
  const baseURL = isServer
    ? (process.env.SERVER_URL || 'http://cms-backend-service.internal.local:5001')
    : window.location.origin;

  axiosConfig.baseURL = baseURL;

  const logUrl = axiosConfig.url?.startsWith('http')
    ? axiosConfig.url
    : `${baseURL}${axiosConfig.url || ''}`;

  devLog(`[API] ${isServer ? 'SERVER' : 'CLIENT'} - Request URL: ${logUrl}`);

  // axios 1.x: per-request `config.headers` is an AxiosHeaders instance with no
  // `.common` bucket (that only exists on axios.defaults.headers). Setting
  // `headers.common.siteId` here threw a TypeError inside the interceptor, so
  // every client request was aborted before it left the browser (SCRUM-710,
  // regression from the axios 0.x→1.x upgrade in SCRUM-422). Set directly.
  // SCRUM-1850: only when the caller passed none — the score preview asks for a
  // site other than the current one, and AxiosHeaders is case-insensitive, so
  // an unconditional set overwrote its `siteid` (the lib/api.js rule, SCRUM-744).
  if (!axiosConfig.headers.has("siteId")) {
    axiosConfig.headers.siteId = configCurrentSite.CURRENT_SITE_ID;
  }
  axiosConfig.headers.siteDomain = configCurrentSite.CURRENT_DOMAIN_SITE;
  return axiosConfig;
});

/** A request parked behind the refresh, told whether the refresh succeeded. */
type RefreshSubscriber = (succeeded: boolean) => void;

let isRefreshing = false;
let refreshSubscribers: RefreshSubscriber[] = [];

const subscribeToTokenRefresh = (cb: RefreshSubscriber) => {
  refreshSubscribers.push(cb);
};

const notifyTokenRefreshed = (succeeded: boolean) => {
  refreshSubscribers.forEach((cb) => cb(succeeded));
  refreshSubscribers = [];
};

const clearLocalAuth = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('role');
  localStorage.removeItem('permission');
  // SCRUM-1471 removed the only writer of `currentsite` (SiteSelector), so there is
  // nothing left for logout to clear.
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const statusCode = error?.response?.status;
    const requestUrl = originalRequest?.url || '';

    if (!originalRequest || statusCode !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (requestUrl.includes('/api/users/refresh-token') || requestUrl.includes('/api/users/auth')) {
      return Promise.reject(error);
    }

    if (typeof window === 'undefined') {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeToTokenRefresh((succeeded) => {
          if (!succeeded) {
            reject(error);
            return;
          }
          resolve(api(originalRequest));
        });
      });
    }

    isRefreshing = true;
    try {
      await api.post('/api/users/refresh-token');
      notifyTokenRefreshed(true);
      return api(originalRequest);
    } catch (refreshError) {
      clearLocalAuth();
      notifyTokenRefreshed(false);
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth/login';
        // Stop the current promise chain because navigation is in progress.
        return new Promise(() => {});
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
export default api;
